import React, { useState } from 'react';
import { Routes, Route, Link, useParams, useNavigate } from 'react-router-dom';
import { Bike, MapPin, Phone, Package, QrCode, Wallet, Landmark, CheckCircle2, AlertTriangle, Truck, LogOut, Banknote, History, ChevronRight } from 'lucide-react';
import { useApp } from '../context/AppContext';
import { useAuth } from '../context/AuthContext';
import { StatusBadge } from '../components/commerce/StatusBadge';
import { EmptyState } from '../components/commerce/EmptyState';
import { Logo } from '../components/commerce/Logo';
import { fmtRM2, timeAgo, KELATE_BANK, KELATE_TNG } from '../data/mockData';
import { Switch } from '../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { toast } from 'sonner';

function useRiderKey() {
  const { user } = useAuth();
  return user?.riderId || 'r1';
}

function RiderStats() {
  const { deliveries, cashEntries } = useApp();
  const riderKey = useRiderKey();
  const mine = deliveries.filter((d) => d.riderId === riderKey);
  const done = mine.filter((d) => d.deliveryStatus === 'DELIVERED');
  const today = new Date().toISOString().slice(0, 10);
  const doneToday = done.filter((d) => (d.completedAt || d.createdAt || '').slice(0, 10) === today);
  const cashOnHand = cashEntries.filter((c) => c.riderId === riderKey && c.status === 'AWAITING_HANDOVER').reduce((s, c) => s + c.amount, 0);
  return (
    <div className="grid grid-cols-3 gap-2" data-testid="rider-stats">
      <div className="rounded-2xl bg-kelate-surface p-3 text-center shadow-[0_1px_4px_rgba(81,68,55,0.08)]">
        <Banknote size={15} className="mx-auto text-kelate-wood" />
        <div className="mt-1 text-base font-bold text-kelate-darkwood" data-testid="cash-on-hand">{fmtRM2(cashOnHand)}</div>
        <div className="text-[10px] uppercase tracking-wide text-kelate-soft">Cash on hand</div>
      </div>
      <div className="rounded-2xl bg-kelate-surface p-3 text-center shadow-[0_1px_4px_rgba(81,68,55,0.08)]">
        <CheckCircle2 size={15} className="mx-auto text-kelate-sage" />
        <div className="mt-1 text-base font-bold text-kelate-darkwood" data-testid="completed-today">{doneToday.length}</div>
        <div className="text-[10px] uppercase tracking-wide text-kelate-soft">Done today</div>
      </div>
      <div className="rounded-2xl bg-kelate-surface p-3 text-center shadow-[0_1px_4px_rgba(81,68,55,0.08)]">
        <History size={15} className="mx-auto text-kelate-soft" />
        <div className="mt-1 text-base font-bold text-kelate-darkwood" data-testid="completed-total">{done.length}</div>
        <div className="text-[10px] uppercase tracking-wide text-kelate-soft">All time</div>
      </div>
    </div>
  );
}

function JobCard({ job }) {
  return (
    <Link to={`/delivery/jobs/${job.id}`} data-testid={`job-card-${job.deliveryNumber}`} className="block rounded-3xl bg-kelate-surface p-4 shadow-[0_1px_4px_rgba(81,68,55,0.08)] transition-all active:scale-[0.99]">
      <div className="flex items-center justify-between">
        <span className="text-sm font-semibold text-kelate-ink">{job.deliveryNumber}</span>
        <StatusBadge status={job.deliveryStatus} />
      </div>
      <div className="mt-2.5 space-y-1.5 text-xs text-kelate-soft">
        <div className="flex items-center gap-1.5"><Package size={13} className="shrink-0 text-kelate-wood" /> <span className="truncate">Pick up: <b className="text-kelate-ink">{job.merchantName}</b></span></div>
        <div className="flex items-center gap-1.5"><MapPin size={13} className="shrink-0 text-kelate-sage" /> <span className="truncate">{job.deliveryAddress}</span></div>
      </div>
      <div className="mt-3 flex items-center justify-between border-t border-kelate-line/60 pt-3">
        <span className="text-sm font-bold text-kelate-darkwood">{fmtRM2(job.amountDue)} <span className="text-[10px] font-medium uppercase tracking-wide text-kelate-soft">{job.paymentMethod === 'TNG_QR' ? 'TNG QR' : job.paymentMethod === 'BANK_TRANSFER' ? 'Bank' : 'Cash'}</span></span>
        <ChevronRight size={15} className="text-kelate-line" />
      </div>
    </Link>
  );
}

function JobsList() {
  const { deliveries } = useApp();
  const riderKey = useRiderKey();
  const mine = deliveries.filter((d) => d.riderId === riderKey && !['DELIVERED', 'FAILED'].includes(d.deliveryStatus));
  return (
    <section>
      <h2 className="mb-2 text-sm font-semibold text-kelate-ink">Active Jobs ({mine.length})</h2>
      <div className="space-y-2.5">
        {mine.map((j) => <JobCard key={j.id} job={j} />)}
        {mine.length === 0 && <EmptyState icon={Bike} title="No active jobs" description="New assignments will appear here with a notification." />}
      </div>
    </section>
  );
}

function HistoryList() {
  const { deliveries } = useApp();
  const riderKey = useRiderKey();
  const done = deliveries.filter((d) => d.riderId === riderKey && ['DELIVERED', 'FAILED'].includes(d.deliveryStatus));
  return (
    <section data-testid="delivery-history">
      <h2 className="mb-2 text-sm font-semibold text-kelate-ink">History</h2>
      <div className="space-y-2">
        {done.map((j) => (
          <Link key={j.id} to={`/delivery/jobs/${j.id}`} data-testid={`history-${j.deliveryNumber}`} className="flex items-center justify-between rounded-2xl bg-kelate-surface p-3.5 shadow-[0_1px_4px_rgba(81,68,55,0.08)]">
            <div>
              <div className="text-sm font-medium text-kelate-ink">{j.deliveryNumber} <span className="text-xs font-normal text-kelate-soft">· {j.customerName}</span></div>
              <div className="text-[11px] text-kelate-soft">{timeAgo(j.completedAt || j.createdAt)} · {fmtRM2(j.amountDue)} {j.paymentMethod === 'CASH' ? 'cash' : j.paymentMethod === 'TNG_QR' ? 'TNG' : 'bank'}</div>
            </div>
            <StatusBadge status={j.deliveryStatus} />
          </Link>
        ))}
        {done.length === 0 && <p className="text-sm text-kelate-soft">Nothing completed yet.</p>}
      </div>
    </section>
  );
}

function JobDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { deliveries, riderAction } = useApp();
  const job = deliveries.find((d) => d.id === id);
  const [confirmCash, setConfirmCash] = useState(false);
  const [showQr, setShowQr] = useState(false);
  const [showBank, setShowBank] = useState(false);
  if (!job) return <EmptyState icon={Package} title="Job not found" actionLabel="My Jobs" actionTo="/delivery" />;

  const doAction = async (action, payload) => {
    const res = await riderAction(job.id, action, payload);
    if (res?.error === 'payment-not-collected') {
      toast.error('Payment not recorded', { description: 'Collect payment before completing delivery. Admin has been alerted (CRITICAL).' });
    }
  };

  const atPaymentStage = ['ARRIVED', 'OUT_FOR_DELIVERY'].includes(job.deliveryStatus);
  const paymentDone = ['COLLECTED_BY_DRIVER', 'COLLECTED', 'VERIFICATION_REQUIRED', 'CONFIRMED'].includes(job.paymentStatus);

  let primaryAction = null;
  if (job.pickupStatus === 'ASSIGNED') primaryAction = { testId: 'mark-picked-up', label: 'Mark Picked Up', icon: Package, run: () => doAction('pickup') };
  else if (job.deliveryStatus === 'PICKED_UP') primaryAction = { testId: 'start-delivery', label: 'Start Delivery', icon: Truck, run: () => doAction('start') };
  else if (job.deliveryStatus === 'OUT_FOR_DELIVERY' && !paymentDone) primaryAction = { testId: 'arrived-btn', label: 'Arrived at Customer', icon: MapPin, run: () => doAction('arrive'), outline: true };
  else if (atPaymentStage && paymentDone) primaryAction = { testId: 'complete-delivery', label: 'Complete Delivery', icon: CheckCircle2, run: () => { doAction('complete'); navigate('/delivery'); }, success: true };

  const btn = "flex w-full items-center justify-center gap-2 rounded-2xl py-4 text-base font-semibold min-h-[52px]";

  return (
    <div data-testid="delivery-job-detail" className="space-y-4 pb-32">
      <div className="rounded-3xl bg-kelate-surface p-4 shadow-[0_1px_4px_rgba(81,68,55,0.08)]">
        <div className="flex items-center justify-between">
          <h1 className="text-lg font-semibold text-kelate-ink" data-testid="job-number">{job.deliveryNumber}</h1>
          <StatusBadge status={job.deliveryStatus} />
        </div>
        <div className="mt-3 space-y-3 text-sm">
          <div className="rounded-2xl bg-secondary/50 p-3.5">
            <div className="text-[10px] font-semibold uppercase tracking-widest text-kelate-soft">Pickup</div>
            <div className="mt-1 font-medium text-kelate-ink">{job.merchantName}</div>
            <div className="text-xs text-kelate-soft">{job.pickupAddress}</div>
          </div>
          <div className="rounded-2xl bg-secondary/50 p-3.5">
            <div className="text-[10px] font-semibold uppercase tracking-widest text-kelate-soft">Customer</div>
            <div className="mt-1 font-medium text-kelate-ink">{job.customerName}</div>
            <div className="text-xs text-kelate-soft">{job.deliveryAddress}</div>
            <a href={`tel:${job.customerPhone}`} data-testid="contact-customer" className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-kelate-surface px-3 py-1.5 text-xs font-medium text-kelate-wood"><Phone size={12} /> {job.customerPhone}</a>
          </div>
          <div className="text-xs text-kelate-soft">Products: <span className="text-kelate-ink">{job.products}</span></div>
        </div>
      </div>

      <div className="rounded-3xl bg-kelate-surface p-4 shadow-[0_1px_4px_rgba(81,68,55,0.08)]" data-testid="collection-card">
        <div className="flex items-center justify-between">
          <span className="text-sm font-semibold text-kelate-ink">Amount to Collect</span>
          <span className="text-2xl font-bold text-kelate-darkwood" data-testid="amount-due">{fmtRM2(job.amountDue)}</span>
        </div>
        <div className="mt-1 flex items-center gap-2 text-xs text-kelate-soft">
          Method: <b className="text-kelate-ink">{job.paymentMethod === 'TNG_QR' ? 'TNG QR' : job.paymentMethod === 'BANK_TRANSFER' ? 'Bank Transfer' : 'Cash'}</b> · <StatusBadge status={job.paymentStatus} />
        </div>

        {atPaymentStage && !paymentDone && job.paymentMethod === 'CASH' && (
          <button data-testid="cash-received-btn" onClick={() => setConfirmCash(true)} className={`${btn} mt-4 bg-kelate-darkwood text-kelate-surface`}>
            <Wallet size={17} /> CASH RECEIVED
          </button>
        )}
        {atPaymentStage && !paymentDone && job.paymentMethod === 'TNG_QR' && (
          <div className="mt-4 space-y-2">
            <button data-testid="show-qr-btn" onClick={() => setShowQr(true)} className={`${btn} border border-kelate-darkwood text-kelate-darkwood`}>
              <QrCode size={17} /> Show Payment QR
            </button>
            <button data-testid="confirm-tng-btn" onClick={() => { doAction('collect', { method: 'TNG_QR' }); toast.success('TNG payment confirmed', { description: 'Finance will verify the collection.' }); }} className={`${btn} bg-kelate-darkwood text-kelate-surface`}>
              Confirm TNG Payment
            </button>
          </div>
        )}
        {atPaymentStage && !paymentDone && job.paymentMethod === 'BANK_TRANSFER' && (
          <div className="mt-4 space-y-2">
            <button data-testid="show-bank-btn" onClick={() => setShowBank(true)} className={`${btn} border border-kelate-darkwood text-kelate-darkwood`}>
              <Landmark size={17} /> Show Bank Details
            </button>
            <button data-testid="confirm-transfer-btn" onClick={() => { doAction('collect', { method: 'BANK_TRANSFER' }); toast.success('Transfer marked received', { description: 'Finance will verify the collection.' }); }} className={`${btn} bg-kelate-darkwood text-kelate-surface`}>
              Confirm Transfer Received
            </button>
          </div>
        )}
        {paymentDone && <div className="mt-4 rounded-2xl bg-[#DCE5D8] px-4 py-3 text-center text-sm font-medium text-[#3E5232]">Payment recorded</div>}
      </div>

      {['OUT_FOR_DELIVERY', 'ARRIVED'].includes(job.deliveryStatus) && (
        <button data-testid="fail-delivery" onClick={() => { doAction('fail', { reason: 'Customer unavailable' }); navigate('/delivery'); }} className="flex w-full items-center justify-center gap-2 rounded-2xl border border-kelate-red/40 py-3 text-xs font-medium text-kelate-red">
          <AlertTriangle size={13} /> Delivery Failed
        </button>
      )}
      {job.deliveryStatus === 'DELIVERED' && (
        <div className="rounded-2xl bg-[#DCE5D8] px-4 py-3.5 text-center text-sm font-medium text-[#3E5232]">Delivery completed. Terima kasih!</div>
      )}

      {primaryAction && (
        <div className="fixed inset-x-0 bottom-0 z-20 mx-auto max-w-md border-t border-kelate-line bg-kelate-surface/95 p-3 backdrop-blur" data-testid="job-action-bar">
          <button
            data-testid={primaryAction.testId}
            onClick={primaryAction.run}
            className={`${btn} ${primaryAction.success ? 'bg-kelate-sage text-white' : primaryAction.outline ? 'border border-kelate-darkwood text-kelate-darkwood' : 'bg-kelate-darkwood text-kelate-surface'}`}
          >
            <primaryAction.icon size={18} /> {primaryAction.label}
          </button>
        </div>
      )}

      <Dialog open={confirmCash} onOpenChange={setConfirmCash}>
        <DialogContent className="bg-kelate-surface border-kelate-line max-w-xs" data-testid="cash-confirm-modal">
          <DialogHeader>
            <DialogTitle className="text-kelate-ink">Confirm Cash</DialogTitle>
            <DialogDescription className="text-kelate-soft">Confirm {fmtRM2(job.amountDue)} cash has been received?</DialogDescription>
          </DialogHeader>
          <div className="flex gap-2">
            <button onClick={() => setConfirmCash(false)} className="min-h-[48px] flex-1 rounded-2xl border border-kelate-line py-3 text-sm text-kelate-soft">Cancel</button>
            <button data-testid="confirm-cash-btn" onClick={() => { doAction('collect', { method: 'CASH' }); setConfirmCash(false); toast.success('Cash collected', { description: 'Hand over to KELATE finance for reconciliation.' }); }} className="min-h-[48px] flex-1 rounded-2xl bg-kelate-darkwood py-3 text-sm font-medium text-kelate-surface">Confirm</button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={showQr} onOpenChange={setShowQr}>
        <DialogContent className="bg-kelate-surface border-kelate-line max-w-xs" data-testid="demo-qr-modal">
          <DialogHeader>
            <DialogTitle className="text-center text-kelate-ink">TNG QR — {fmtRM2(job.amountDue)}</DialogTitle>
          </DialogHeader>
          <div className="mx-auto grid h-48 w-48 grid-cols-8 gap-0 overflow-hidden rounded-sm border border-kelate-line" aria-label="Payment QR">
            {Array.from({ length: 64 }).map((_, i) => (
              <span key={i} className={`${(i * 7 + Math.floor(i / 8) * 3) % 5 < 2 ? 'bg-kelate-ink' : 'bg-kelate-surface'}`} />
            ))}
          </div>
          <p className="text-center text-[11px] text-kelate-soft">Customer scans this QR on-site to pay.<br />{KELATE_TNG.holder} · TNG eWallet: {KELATE_TNG.number}</p>
        </DialogContent>
      </Dialog>

      <Dialog open={showBank} onOpenChange={setShowBank}>
        <DialogContent className="bg-kelate-surface border-kelate-line max-w-xs" data-testid="bank-details-modal">
          <DialogHeader>
            <DialogTitle className="text-kelate-ink">Bank Transfer Details</DialogTitle>
            <DialogDescription className="text-kelate-soft">Only shown during collection. Configured in Admin Finance settings.</DialogDescription>
          </DialogHeader>
          <div className="rounded-sm bg-secondary/60 p-3 font-mono text-xs text-kelate-ink">
            {KELATE_BANK.holder}<br />{KELATE_BANK.account}<br />Amount: {fmtRM2(job.amountDue)}<br />Ref: {job.orderNumber}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default function DeliveryApp() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { unreadCount, riderAvailability, setRiderAvailability } = useApp();
  if (user === null) return null;
  if (!user || user.role !== 'rider') { navigate('/account/login?redirect=/delivery'); return null; }
  const riderKey = user.riderId || 'r1';
  const available = riderAvailability[riderKey] !== undefined ? riderAvailability[riderKey] : user.riderAvailable !== false;
  const jobs = unreadCount('rider');
  return (
    <div data-testid="delivery-app" className="mx-auto min-h-screen max-w-md bg-kelate-bg px-4 pb-16 pt-4">
      <header className="mb-4 flex items-center justify-between">
        <div>
          <Logo className="h-7" testId="delivery-logo" />
          <div className="text-xs text-kelate-soft">{user.name} · Kota Bharu zone {jobs > 0 && <span className="ml-1 rounded-full bg-kelate-red px-1.5 text-[10px] font-semibold text-white">{jobs} new</span>}</div>
        </div>
        <button data-testid="rider-logout" onClick={async () => { await logout(); navigate('/'); }} className="rounded-xl p-2.5 text-kelate-soft hover:bg-secondary"><LogOut size={17} /></button>
      </header>
      <div className={`mb-4 flex items-center justify-between rounded-2xl px-4 py-3 shadow-[0_1px_4px_rgba(81,68,55,0.08)] ${available ? 'bg-kelate-surface' : 'bg-secondary/60'}`} data-testid="rider-availability">
        <div className="flex items-center gap-2.5">
          <span className={`h-2.5 w-2.5 rounded-full ${available ? 'bg-kelate-sage' : 'bg-kelate-soft/50'}`} />
          <div>
            <div className="text-sm font-semibold text-kelate-ink">{available ? "You're online" : "You're offline"}</div>
            <div className="text-[11px] text-kelate-soft">{available ? 'Admin can assign new jobs to you' : 'No new jobs will be assigned'}</div>
          </div>
        </div>
        <Switch data-testid="rider-availability-toggle" checked={available} onCheckedChange={(v) => { setRiderAvailability(riderKey, v); toast.success(v ? "You're online" : "You're offline"); }} />
      </div>
      <Routes>
        <Route index element={<div className="space-y-6"><RiderStats /><JobsList /><HistoryList /></div>} />
        <Route path="jobs/:id" element={<JobDetail />} />
      </Routes>
    </div>
  );
}
